'use client';

import React from 'react';
import DOMPurify from 'isomorphic-dompurify';

// Sanitization config for message previews - allows basic formatting but prevents XSS
// For previews, we keep everything inline (no line breaks)
// Note: 'span' is excluded as it doesn't add visual formatting and clutters previews
export const PREVIEW_SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'u', 's'] as string[],
  ALLOWED_ATTR: ['class', 'style'] as string[],
  FORBID_TAGS: [
    'script',
    'style',
    'form',
    'input',
    'button',
    'object',
    'embed',
    'iframe',
    'base',
    'link',
    'meta',
    'br',
    'p',
    'div',
  ] as string[],
  FORBID_ATTR: [
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'onmouseout',
    'onfocus',
    'onblur',
    'onchange',
    'onsubmit',
  ] as string[],
};

// Helper function to sanitize and make preview inline (single line)
export const sanitizePreviewForInline = (html?: string): string => {
  if (!html) return '';

  // First sanitize with DOMPurify
  let sanitized = DOMPurify.sanitize(html, PREVIEW_SANITIZE_CONFIG) as string;

  // Remove any remaining line breaks and convert block elements to spaces
  sanitized = sanitized
    // Defect (see message-preview.test.tsx): all five tag-replacement regexes below
    // are dead. PREVIEW_SANITIZE_CONFIG's FORBID_TAGS already strips 'br', 'p' and
    // 'div' outright, so DOMPurify.sanitize above never leaves one in `sanitized`
    // for these regexes to match — a manual line break previews with its words run
    // together instead of separated by a space.
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br> with space
    .replace(/<\/p>/gi, ' ') // Replace closing </p> with space
    .replace(/<\/div>/gi, ' ') // Replace closing </div> with space
    .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
    .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
    .replace(/<span[^>]*>/gi, '') // Remove opening <span> tags (not needed for previews)
    .replace(/<\/span>/gi, '') // Remove closing </span> tags
    .replace(/\n/g, ' ') // Replace newlines with space
    .replace(/\r/g, ' ') // Replace carriage returns with space
    .replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim(); // Trim whitespace

  // Decode a few common HTML entities so previews don't show &amp; etc.
  sanitized = sanitized
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

  return sanitized;
};

interface MessagePreviewProps {
  html?: string;
  className?: string;
  /**
   * Optional max length. If provided, the preview will be truncated and an ellipsis appended.
   */
  maxLength?: number;
  /**
   * Element type to render as. Defaults to 'p'.
   */
  as?: 'p' | 'span' | 'div';
}

export const MessagePreview: React.FC<MessagePreviewProps> = ({
  html,
  className,
  maxLength,
  as = 'p',
}) => {
  const inline = sanitizePreviewForInline(html);
  const text =
    typeof maxLength === 'number' && maxLength > 0 && inline.length > maxLength
      ? `${inline.slice(0, maxLength)}…`
      : inline;

  const Component = as;

  // Render as HTML to preserve formatting (bold, italic, etc.) while keeping it safe
  return <Component className={className} dangerouslySetInnerHTML={{ __html: text }} />;
};

MessagePreview.displayName = 'MessagePreview';
